'use client'

import { CheckCircle2, Clock } from 'lucide-react'

interface StepConfig {
  number: number
  title: string
  estimatedMinutes?: number
  isRequired?: boolean
}

interface IntakeProgressProps {
  currentStep: number
  totalSteps?: number
  steps?: StepConfig[]
  showEstimate?: boolean
  isCompleted?: boolean
}

const defaultSteps: StepConfig[] = [
  { number: 1, title: 'Basic Info', estimatedMinutes: 2, isRequired: true },
  { number: 2, title: 'Health History', estimatedMinutes: 3, isRequired: true },
  { number: 3, title: 'Questionnaires', estimatedMinutes: 5, isRequired: true },
  { number: 4, title: 'Lifestyle', estimatedMinutes: 2, isRequired: false },
  { number: 5, title: 'Review & Submit', estimatedMinutes: 1, isRequired: true },
]

export default function IntakeProgress({ 
  currentStep, 
  totalSteps = 5,
  steps = defaultSteps,
  showEstimate = true,
  isCompleted = false
}: IntakeProgressProps) {
  const normalizedCurrentStep = Math.min(Math.max(currentStep, 1), totalSteps)

  // Calculate completion percentage
  const completionPercentage = isCompleted
    ? 100
    : Math.round(((normalizedCurrentStep - 1) / totalSteps) * 100)
  
  // Calculate estimated time remaining
  const remainingSteps = isCompleted
    ? []
    : steps.filter(step => step.number >= normalizedCurrentStep)
  const estimatedMinutesRemaining = remainingSteps.reduce(
    (total, step) => total + (step.estimatedMinutes || 2), 
    0
  )

  return (
    <div className="w-full max-w-4xl mx-auto mb-8">
      {/* Progress Header - Percentage and Time Estimate */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-white">
            Step {isCompleted ? totalSteps : normalizedCurrentStep} of {totalSteps}
          </span>
          <span className="text-sm text-white/60">
            • {completionPercentage}% Complete
          </span>
        </div>
        {showEstimate && !isCompleted && estimatedMinutesRemaining > 0 && (
          <div className="flex items-center gap-1.5 text-sm text-white/60">
            <Clock className="h-4 w-4" />
            <span>~{estimatedMinutesRemaining} min remaining</span>
          </div>
        )}
      </div>

      {/* Overall Progress Bar */}
      <div className="relative h-2 bg-white/10 rounded-full mb-6 overflow-hidden">
        <div 
          className="absolute top-0 left-0 h-full bg-[#4A8A9A] rounded-full transition-all duration-500"
          style={{ width: `${completionPercentage}%` }}
        />
      </div>

      {/* Step Circles with Labels */}
      <div className="relative">
        {/* Connecting Line */}
        <div className="absolute top-5 left-0 right-0 h-0.5 bg-white/10" />
        <div 
          className="absolute top-5 left-0 h-0.5 bg-[#4A8A9A] transition-all duration-500"
          style={{ width: `${isCompleted ? 100 : ((normalizedCurrentStep - 1) / (totalSteps - 1)) * 100}%` }}
        />
        
        {/* Step Circles */}
        <div className="relative flex justify-between">
          {steps.map((step) => {
            const stepCompleted = isCompleted ? step.number <= totalSteps : step.number < normalizedCurrentStep
            const stepActive = !isCompleted && step.number === normalizedCurrentStep

            return (
            <div key={step.number} className="flex flex-col items-center">
              <div 
                className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
                  stepCompleted
                    ? 'bg-[#4A8A9A] text-white' 
                    : stepActive
                    ? 'bg-[#4A8A9A] text-white ring-4 ring-[#4A8A9A]/30'
                    : 'bg-white/5 border-2 border-white/20 text-white/40'
                }`}
              >
                {stepCompleted ? (
                  <CheckCircle2 className="h-6 w-6" />
                ) : (
                  <span>{step.number}</span>
                )}
              </div>
              
              {/* Step Label and Time Estimate (Desktop) */}
              <div className="mt-2 text-center hidden sm:block">
                <div className={`text-xs font-medium ${
                  stepActive
                    ? 'text-[#4A8A9A]' 
                    : stepCompleted
                    ? 'text-[#4A8A9A]' 
                    : 'text-white/40'
                }`}>
                  {step.title}
                </div>
                {step.estimatedMinutes && !isCompleted && step.number >= normalizedCurrentStep && (
                  <div className="text-[10px] text-white/30 mt-0.5">
                    ~{step.estimatedMinutes} min
                  </div>
                )}
                {step.isRequired === false && !isCompleted && step.number >= normalizedCurrentStep && (
                  <span className="text-[10px] text-white/30 italic">Optional</span>
                )}
              </div>
            </div>
          )})}
        </div>
      </div>

      {/* Mobile Step Indicator */}
      <div className="sm:hidden mt-4 text-center">
        <div className="text-sm font-medium text-white">
          {isCompleted ? 'Completed' : steps[normalizedCurrentStep - 1]?.title}
        </div>
        {!isCompleted && steps[normalizedCurrentStep - 1]?.estimatedMinutes && (
          <div className="text-xs text-white/60 mt-1">
            ~{steps[normalizedCurrentStep - 1]?.estimatedMinutes} min for this step
          </div>
        )}
      </div>
    </div>
  )
}
